import { useCallback, useMemo, useState } from 'react';

import { initialStoreState } from 'features/store/constants';
import { initStore } from 'features/store/utils';
import { isEqual } from 'lodash-es';

import { IRestaurant } from '@rbi-ctg/store';
import { updateLaunchDarklyUser } from 'state/launchdarkly/launchdarkly-core';
import { useServiceModeContext } from 'state/service-mode';
import { SyncStorageKey, syncStorage } from 'utils/environment/sync-storage';
import { preloadedStoreInfo } from 'utils/store-info';

import { IStoreContext, IUseStore } from '../types';

import { resetLastTimeStoreUpdated } from './reset-last-time-store-updated';

export const useStore = (): IUseStore => {
  const { serviceMode } = useServiceModeContext();
  const preloaded = useMemo(() => preloadedStoreInfo(), []);

  const [storeState, setStoreState] = useState<IRestaurant | null>(
    initStore(preloaded, serviceMode)
  );

  const handleSetStoreState = useCallback<IUseStore['setStore']>(async newStore => {
    syncStorage.setObject(SyncStorageKey.STORE, {
      store: newStore,
    });

    await updateLaunchDarklyUser(
      {
        storeNumber: newStore?.number ?? '',
        storeId: newStore?.number ?? '',
        storeCity: newStore?.physicalAddress?.city ?? '',
        storeCountry: newStore?.physicalAddress?.country ?? '',
        storePostalCode: newStore?.physicalAddress?.postalCode ?? '',
        storeStateProvince: newStore?.physicalAddress?.stateProvince ?? '',
        storeStateProvinceShort: newStore?.physicalAddress?.stateProvinceShort ?? '',
        storeFranchiseGroupName: newStore?.franchiseGroupName ?? '',
        storePosVendor: newStore?.pos?.vendor ?? '',
        posVendor: newStore?.pos?.vendor ?? '',
        storeVatNumber: newStore?.vatNumber ?? '',
      },
      'store change'
    );

    if (!newStore?._id) {
      setStoreState(initialStoreState);
      return;
    }

    setStoreState(prevStore => {
      if (isEqual(newStore, prevStore)) {
        return prevStore;
      }
      return newStore;
    });

    resetLastTimeStoreUpdated();
  }, []);

  /** Change store back to initial state (excluding prefetch) */
  const resetStoreState = useCallback<IStoreContext['resetStore']>(() => {
    handleSetStoreState(initialStoreState);
  }, [handleSetStoreState]);

  const onConfirmStoreChange: IUseStore['onConfirmStoreChange'] = useCallback(
    async args => {
      if (!args) {
        return;
      }
      const { newStore, callback } = args;
      const store: IRestaurant = { ...newStore };

      await handleSetStoreState(store);

      if (typeof callback === 'function') {
        await callback();
      }
    },
    [handleSetStoreState]
  );

  return useMemo(
    () => ({
      resetStore: resetStoreState,
      setStore: handleSetStoreState,
      store: storeState || initialStoreState,
      onConfirmStoreChange,
    }),
    [onConfirmStoreChange, resetStoreState, handleSetStoreState, storeState]
  );
};
